import React from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import { CTAFooter } from "@/pages/Prestations";
import PhotoGallery from "@/components/PhotoGallery";
import { useSeo, JsonLd } from "@/lib/seo";
import { CatalogPackages } from "@/components/CatalogPackages";
import {
  Mic2, Music, Building2, Landmark, Sparkles, Instagram, Facebook, Youtube,
  ArrowRight, Phone, UserPlus, Check, Star,
} from "lucide-react";

const MAXENCE_IMG =
  "https://customer-assets.emergentagent.com/job_animation-prestige/artifacts/u13ufysz_97A37581-3265-4911-8513-26FBC45C9966.png";
const MAXENCE_PHOTOS = Array.from({ length: 6 }, (_, i) => `/assets/maxence/maxence-${String(i + 1).padStart(2, "0")}.jpg`);

const FORMULES = [
  {
    name: "Chanteur Live",
    badge: "Live exclusif",
    price: "499 €",
    duration: "2h",
    pitch: "Un chanteur renommé, ayant participé à The Voice sur TF1 et élu Prodige, se produit en live pour votre événement.",
    features: [
      "Prestation : 2h",
      "Chanteur en live",
      "Répertoire varié et personnalisable",
      "Sonorisation incluse",
    ],
  },
  {
    name: "Chanteur + Karaoké",
    badge: "Le plus choisi",
    featured: true,
    price: "699 €",
    duration: "2h",
    pitch: "Une formule complète mêlant concert live et karaoké interactif, animée par un artiste reconnu (The Voice — Prodige).",
    features: [
      "Prestation : 2h",
      "Chanteur en live",
      "Karaoké animé",
      "Micros pour les participants",
      "Sonorisation incluse",
      "Animation du public",
    ],
  },
  {
    name: "Karaoké animé",
    badge: "Convivial",
    price: "399 €",
    duration: "2h",
    pitch: "Une animation karaoké professionnelle animée par un chanteur renommé, garantissant une ambiance festive et conviviale.",
    features: [
      "Prestation : 2h",
      "Animation karaoké",
      "Large catalogue de chansons",
      "Micros + sonorisation inclus",
      "Animation fluide",
    ],
  },
];

const SECTIONS = [
  {
    icon: Mic2,
    title: "Présentation",
    text:
      "Maxence Berche est un chanteur live qui peut intervenir sur vos événements en complément d'une prestation My Animation. Sa voix apporte une dimension live, élégante et émotionnelle à votre soirée — un véritable plus pour marquer les esprits.",
  },
  {
    icon: Sparkles,
    title: "Chanteur live pour mariage",
    text:
      "Cérémonie, vin d'honneur, premier slow ou ouverture de bal : Maxence Berche sublime les instants émotionnels de votre mariage avec un répertoire adapté à votre univers et à votre histoire.",
  },
  {
    icon: Building2,
    title: "Chanteur live pour entreprise",
    text:
      "Soirées d'entreprise, galas, séminaires, inaugurations : une prestation live élégante pour valoriser votre image et offrir une expérience mémorable à vos collaborateurs ou clients.",
  },
  {
    icon: Landmark,
    title: "Chanteur live pour mairie & événement public",
    text:
      "Fête de la Musique, cérémonies, inaugurations, événements municipaux et associatifs : Maxence Berche s'adapte à tous les formats publics — en duo avec le DJ set My Animation ou en première partie.",
  },
  {
    icon: Music,
    title: "Première partie live + DJ set My Animation",
    text:
      "Une formule complète : Maxence Berche assure une première partie live, puis My Animation prend le relais avec un DJ set festif pour faire danser vos invités jusque tard dans la soirée.",
  },
  {
    icon: UserPlus,
    title: "Formule événementielle sur mesure",
    text:
      "Selon le format, le lieu et le projet, la prestation chanteur live peut être proposée seule ou en complément d'un DJ set My Animation. Nous construisons ensemble la formule qui correspond à votre événement.",
  },
];

// Custom inline TikTok icon (lucide-react doesn't ship one)
const TikTokIcon = ({ size = 18, strokeWidth = 1.4, className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

const SOCIALS = [
  { name: "Instagram", Icon: Instagram, url: "https://www.instagram.com/berchemaxence/", handle: "@berchemaxence" },
  { name: "Facebook", Icon: Facebook, url: "https://www.facebook.com/maxencebercheofficiel/", handle: "maxencebercheofficiel" },
  { name: "YouTube", Icon: Youtube, url: "https://www.youtube.com/watch?v=Vs4OfIKw6VM", handle: "Unchained Melody · The Voice" },
  { name: "TikTok", Icon: TikTokIcon, url: "https://www.tiktok.com/@maxencebercheofficiel", handle: "@maxencebercheofficiel" },
];

export default function MaxenceBerche() {
  useSeo({
    title: "Maxence Berche chanteur live | Événements Hauts-de-France",
    description:
      "Ajoutez une prestation chanteur live à votre événement avec Maxence Berche et My Animation : mariage, entreprise, mairie, soirée privée et événement public dans les Hauts-de-France.",
    path: "/maxence-berche",
    keywords: "chanteur live événement Hauts-de-France, chanteur mariage Nord, chanteur live entreprise Lille, Maxence Berche",
  });

  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Chanteur live — Maxence Berche x My Animation",
    provider: { "@type": "LocalBusiness", name: "My Animation" },
    areaServed: "Hauts-de-France, Nord, Pas-de-Calais, Belgique",
    description:
      "Prestation chanteur live pour mariage, entreprise, mairie et événement public, en complément ou en partenariat avec My Animation.",
  };

  return (
    <>
      <JsonLd data={serviceLd} />
      <PageHero
        eyebrow="Partenaire · Chanteur Live"
        title={<>Maxence Berche — <span className="italic gold-text">chanteur live</span><br/>pour vos événements.</>}
        subtitle="Une voix live pour sublimer vos mariages, soirées privées, événements d'entreprise, mairies et événements publics. Prestation possible seule ou en complément d'un DJ set My Animation, selon le format, le lieu et le projet."
        img={MAXENCE_IMG}
      />

      {/* Portrait + bio courte */}
      <section className="py-20 md:py-28 px-6 md:px-10 lg:px-14 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-5 reveal">
            <div className="relative overflow-hidden">
              <img
                src={MAXENCE_IMG}
                alt="Maxence Berche — chanteur live partenaire de My Animation"
                className="w-full h-[520px] md:h-[640px] object-cover object-top"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5">
                <p className="font-serif-luxe text-2xl italic text-white">Maxence Berche</p>
                <p className="text-[11px] uppercase tracking-[0.25em] text-[#DCB457] mt-1">Chanteur live · Partenaire My Animation</p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 reveal">
            <span className="eyebrow">Une voix live au service de vos émotions</span>
            <h2 className="font-serif-luxe text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.02]">
              Une voix, une scène,<br/>
              <span className="italic gold-text">un moment.</span>
            </h2>
            <p className="text-white/70 text-base md:text-lg leading-relaxed mt-6">
              Ajoutez une prestation chanteur live à votre événement avec Maxence Berche
              et My Animation. Une formule pensée pour les couples, entreprises, mairies
              et collectivités qui souhaitent offrir à leurs invités une expérience
              mémorable et un instant suspendu — avant que la piste de danse ne s'ouvre.
            </p>
            <div className="flex flex-wrap gap-3 mt-10">
              <Link to="/contact" data-testid="maxence-cta-devis-hero" className="btn-gold">
                Demander un devis <ArrowRight size={14} strokeWidth={1.4} />
              </Link>
              <Link to="/contact?prestation=chanteur-live" data-testid="maxence-cta-add-hero" className="btn-ghost-gold">
                Ajouter un chanteur live
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Référence The Voice — Prodige */}
      <section className="py-10 md:py-14 px-6 md:px-10 lg:px-14">
        <div className="max-w-[1100px] mx-auto card-luxe p-6 md:p-8 reveal flex flex-col md:flex-row items-center gap-4 md:gap-6 text-center md:text-left">
          <div className="flex items-center gap-2 shrink-0">
            <Star size={20} className="fill-[#DCB457] text-[#DCB457]" />
            <Star size={20} className="fill-[#DCB457] text-[#DCB457]" />
            <Star size={20} className="fill-[#DCB457] text-[#DCB457]" />
          </div>
          <p className="text-white/85 text-base md:text-lg leading-relaxed">
            Un <span className="font-medium text-[#DCB457]">chanteur renommé</span>,
            ayant participé à <em>The Voice</em> sur TF1 et élu <em>Prodige</em>,
            au service de votre événement.
          </p>
        </div>
      </section>

      {/* Forfaits Maxence Berche — synchronisés avec le catalogue produits */}
      <CatalogPackages
        category="chanteur"
        title="Nos formules 2026"
        subtitle={(n) => (<>{n || "Nos"} formule{n > 1 ? "s" : ""} <span className="italic gold-text">prêtes à briller.</span></>)}
        testidPrefix="maxence-pack-"
        featuredBadgeLabel="Le plus choisi"
        ctaLabel="Pré-réserver cette formule"
        footerNote="Tarifs TTC · Formules combinables avec un DJ set My Animation · Devis personnalisé disponible"
      />

      {/* Réseaux sociaux officiels */}
      <section className="py-16 md:py-20 px-6 md:px-10 lg:px-14" data-testid="socials-section">
        <div className="max-w-[1100px] mx-auto">
          <div className="text-center mb-10 reveal">
            <span className="eyebrow">Réseaux sociaux officiels</span>
            <h2 className="font-serif-luxe text-3xl md:text-4xl mt-3">
              Suivez Maxence Berche <span className="italic gold-text">en ligne.</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {SOCIALS.map(({ name, Icon, url, handle }) => (
              <a
                key={name}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`social-${name.toLowerCase()}`}
                aria-label={`${name} Maxence Berche`}
                className="card-luxe p-7 flex flex-col items-center text-center gap-3 hover:border-[#DCB457]/60 transition group"
              >
                <Icon size={28} strokeWidth={1.2} className="text-[#DCB457] group-hover:scale-110 transition-transform" />
                <p className="font-serif-luxe text-xl">{name}</p>
                <p className="text-[10px] uppercase tracking-[0.22em] text-white/55 break-all">{handle}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Vidéo Prodiges 2021 (vidéo officielle hébergée) */}
      <section className="py-16 md:py-20 px-6 md:px-10 lg:px-14" data-testid="prodiges-video-section">
        <div className="max-w-[1100px] mx-auto card-luxe p-8 md:p-12 reveal">
          <div className="text-center mb-8">
            <span className="eyebrow">Prodiges 2021 · Finale</span>
            <h2 className="font-serif-luxe text-3xl md:text-4xl mt-3">
              Maxence à 16 ans — <span className="italic gold-text">Nessun dorma.</span>
            </h2>
            <p className="text-white/60 mt-3 text-sm max-w-2xl mx-auto leading-relaxed">
              Maxence Berche, 16 ans, interprète <em>Nessun dorma</em> (Turandot, Giacomo Puccini) en finale de Prodiges 2021 (Saison 8).
            </p>
          </div>
          <div className="relative w-full bg-black overflow-hidden" style={{ aspectRatio: "16/9" }}>
            <video
              data-testid="maxence-prodiges-video"
              src="/assets/maxence/video/maxence-prodiges-2021-nessun-dorma.mp4"
              controls
              preload="metadata"
              playsInline
              className="absolute inset-0 w-full h-full object-contain bg-black"
            >
              Votre navigateur ne prend pas en charge la lecture vidéo HTML5.
            </video>
          </div>
        </div>
      </section>

      {/* The Voice — Unchained Melody (audition officielle) */}
      <section className="py-16 md:py-20 px-6 md:px-10 lg:px-14" data-testid="clip-section">
        <div className="max-w-[1100px] mx-auto card-luxe p-8 md:p-12 reveal">
          <div className="text-center mb-8">
            <span className="eyebrow">The Voice · Audition à l'aveugle</span>
            <h2 className="font-serif-luxe text-3xl md:text-4xl mt-3">
              Maxence Berche — <span className="italic gold-text">Unchained Melody.</span>
            </h2>
            <p className="text-white/60 mt-3 text-sm max-w-2xl mx-auto leading-relaxed">
              Audition à l'aveugle de Maxence Berche sur <em>Unchained Melody</em> des Righteous Brothers — The Voice 2024 sur TF1.
            </p>
          </div>
          <div className="relative w-full bg-black overflow-hidden" style={{ aspectRatio: "16/9" }}>
            <iframe
              src="https://www.youtube.com/embed/Vs4OfIKw6VM"
              title="Maxence Berche — Unchained Melody — The Voice 2024 (TF1)"
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              data-testid="maxence-clip-iframe"
            />
          </div>
        </div>
      </section>

      {/* Sections détaillées */}
      <section className="py-16 md:py-24 px-6 md:px-10 lg:px-14 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SECTIONS.map(({ icon: Icon, title, text }) => (
            <div key={title} className="card-luxe p-8 md:p-10 reveal flex flex-col">
              <Icon size={28} strokeWidth={1} className="text-[#DCB457] mb-5" />
              <h2 className="font-serif-luxe text-2xl md:text-3xl">{title}</h2>
              <p className="text-white/65 mt-3 text-[15px] leading-relaxed flex-1">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Galerie Maxence Berche */}
      <PhotoGallery
        images={MAXENCE_PHOTOS}
        eyebrow="En images"
        title={<>Maxence Berche <span className="italic gold-text">en scène.</span></>}
        subtitle="Quelques moments capturés lors de prestations live de Maxence Berche."
        testIdPrefix="maxence-gallery"
      />

      {/* Mention partenariat */}
      <section className="py-12 md:py-16 px-6 md:px-10 lg:px-14">
        <div className="max-w-[1100px] mx-auto card-luxe p-8 md:p-10 reveal text-center">
          <p className="text-[15px] md:text-base text-white/75 leading-relaxed">
            Prestation possible <span className="text-[#DCB457]">seule</span> ou en complément d'un{" "}
            <span className="text-[#DCB457]">DJ set My Animation</span>, selon le format, le lieu et le projet.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#DCB457]/15 py-20 px-6 md:px-10 lg:px-14 max-w-[1400px] mx-auto">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <h3 className="font-serif-luxe text-3xl md:text-4xl max-w-2xl">
            Construisons votre prestation avec <span className="italic gold-text">chanteur live</span>.
          </h3>
          <div className="flex flex-wrap gap-3">
            <Link to="/contact" data-testid="maxence-cta-devis" className="btn-gold">
              Demander un devis <ArrowRight size={14} strokeWidth={1.4} />
            </Link>
            <Link to="/contact?prestation=chanteur-live" data-testid="maxence-cta-add" className="btn-ghost-gold">
              Ajouter un chanteur live à mon événement
            </Link>
            <Link to="/prestations" className="btn-ghost-gold">
              Voir les prestations DJ
            </Link>
            <a href="tel:0769224956" className="btn-ghost-gold">
              <Phone size={14} strokeWidth={1.4} /> Appeler
            </a>
          </div>
        </div>
      </section>

      <CTAFooter />
    </>
  );
}
